import { Draft, PayloadAction } from "@reduxjs/toolkit";
import { ApiReducerKey, RootDispatch, RootState } from "../Store";

/**
 * The base representation of a redux store state. Can be used as base class for any state
 * representations.
 */
export interface ApiState {
    /**
     * The error that occurred during the most recently failed request. Will be reset on the next
     * successful request.
     */
    error?: string;

    /**
     * The current loading state.
     */
    loading: boolean;

    /**
     * Whether the state is currently loading for the first time.
     */
    initialLoading: boolean;

    /**
     * The time the most recent successful request completed.
     */
    loadTime?: number;

    /**
     * The status code of the most recently completed request, whether it was successful or failed.
     */
    statusCode?: number;

    /**
     * The value of the state slice. Will be specified by the subclass.
     */
    value: unknown;

    /**
     * The globally unique name of this state slice. Can not be changed.
     */
    readonly name: string;
}

/**
 * The representation of the payload of a "load failed" action.
 */
export interface LoadFailedPayload {
    /**
     * The error that occurred.
     */
    error: string;

    /**
     * The status code of the request.
     */
    statusCode: number;
}

/**
 * The base representation of the payload of a "load successful" action. Can be used as a base
 * class for any successful response payload.
 */
export interface LoadSuccessfulPayload {
    /**
     * The status code of the request.
     */
    statusCode: number;
}

/**
 * Constants used for creating the action names.
 */
export const LoadStarted = "LoadStarted";
export const LoadFailed = "LoadFailed";
export const ClearState = "ClearState";

/**
 * When dispatched, the passed state will be reset to initial state.
 *
 * @param name The name of the state to reset
 */
export const clearState = (name: ApiReducerKey) => async (
    dispatch: RootDispatch,
    getState: () => RootState
) => {
    dispatch({
        type: ClearState,
        payload: getState()[name].name
    });
};

/**
 * Processes a started request, sets the loading state to true, and clears the error.
 *
 * @param draft The current state
 */
export const reduceLoadStarted = (draft: Draft<ApiState>): void => {
    draft.loading = true;
    draft.error = undefined;

    if (!draft.statusCode) {
        draft.initialLoading = true;
    }
};

/**
 * Processes a failed request. Sets the loading state to false and saves error message and status
 * code.
 *
 * @param draft The current state
 * @param action The "load failed" action
 */
export const reduceLoadFailed = (
    draft: Draft<ApiState>, action: PayloadAction<LoadFailedPayload>
): void => {
    draft.loading = false;
    draft.initialLoading = false;
    draft.error = action.payload.error;
    draft.statusCode = action.payload.statusCode;
};

/**
 * Processes a clear state request. Resets the state to its initial values.
 *
 * @param draft The current state
 * @param action The "clear state" action
 */
export const reduceClearState = (
    draft: Draft<ApiState>, action: PayloadAction<ApiReducerKey, "ClearState">
): void => {
    if (draft.name === action.payload) {
        draft.error = undefined;
        draft.value = undefined;
        draft.loading = false;
        draft.loadTime = undefined;
        draft.initialLoading = false;
        draft.statusCode = undefined;
    }
};
